import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import {
  type Layout,
  ResponsiveGridLayout,
  type ResponsiveLayouts,
  useContainerWidth,
} from 'react-grid-layout';

import { usePageFilterContext } from '@/components/page';
import { cn } from '@/lib/utils';

import DashboardRowWidget from './DashboardRowWidget';
import DashboardWidget from './DashboardWidget';
import { type DashboardType, type DashboardWidgetRowType, type DashboardWidgetType } from './types';

const DASHBOARD_LAYOUT_CLASS = 'c99-dashboard-layout';
const DASHBOARD_LAYOUT_ANIMATED_CLASS = 'c99-dashboard-layout--animated';
const DASHBOARD_WIDGET_FULLSCREEN_CLASS = 'c99-dashboard-widget-fullscreen';
const DASHBOARD_DRAG_HANDLE_CLASS = 'c99-dashboard-draggable-handle';
const DASHBOARD_DRAG_IGNORE_CLASS = 'c99-dashboard-draggable-ignore';

const isRowWidget = (widget: DashboardWidgetType): widget is DashboardWidgetRowType => {
  return 'children' in widget;
};

const BREAKPOINT_MEDIUM = 'md';
const BREAKPOINT_SMALL = 'xxs';

const breakpoints = {
  [BREAKPOINT_MEDIUM]: 769, // 1 more than regular ipad in portrait
  [BREAKPOINT_SMALL]: 0,
};
const cols = { [BREAKPOINT_MEDIUM]: 24, [BREAKPOINT_SMALL]: 1 };

const buildLayout = (data: DashboardType): Layout => {
  return data.widgets.map(({ id, gridPos }) => ({ i: id, ...gridPos }));
};

const updatePositions = (dashboard: DashboardType, gridLayout: Layout) => {
  return {
    ...dashboard,
    widgets: dashboard.widgets.map((widget, index) => {
      const newPosition = gridLayout[index];
      const { x, y, w, h } = newPosition;

      return {
        ...widget,
        gridPos: { x, y, w, h },
      };
    }),
  };
};

type Props = {
  className?: string;
  dashboard: DashboardType;
  isEditable?: boolean;
  isInteractive?: boolean;
  onLayoutChange?: (newDashboard: DashboardType) => void;
  rowHeight?: number;
};

const DashboardLayout = ({
  className = '',
  dashboard,
  rowHeight = 40,
  isEditable = true,
  onLayoutChange,
}: Props) => {
  const { pageFilters } = usePageFilterContext();
  const [animated, setAnimated] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [fullscreenWidgetId, setFullScreenWidgetId] = useState<string | null>(null);
  const { containerRef, width } = useContainerWidth();
  const draggableHandleSelector = DASHBOARD_DRAG_HANDLE_CLASS;
  const draggableIgnoreSelector = DASHBOARD_DRAG_IGNORE_CLASS;

  const handleLayoutChange = (layout: Layout, layouts: ResponsiveLayouts) => {
    const dashLayout = layouts[BREAKPOINT_MEDIUM] || layout;
    const updatedDashboard = updatePositions(dashboard, dashLayout);

    onLayoutChange?.(updatedDashboard);
  };

  const handleWidgetExpand = (isExpanded: boolean, widget: DashboardWidgetType) => {
    setFullScreenWidgetId(isExpanded ? widget.id : null);
  };

  useEffect(() => {
    // react-grid-layout will animate all cells to move to their first position on initial render
    // which looks a little janky. Setting a timer which enables position animation after initial
    // render fixes this -- now cells will just immediately appear in their initial position.
    const timeout = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timeout);
  }, []);

  const children = useMemo(
    () =>
      dashboard.widgets.map((widget) => {
        const style: CSSProperties = {};
        let itemClassName = '';
        const isFullScreen = widget.id === fullscreenWidgetId;

        if (fullscreenWidgetId) {
          if (isFullScreen) {
            itemClassName = 'react-grid-item--fullScreen';
            style.width = '100%';
            style.height = '100%';
            style.display = 'block';
          } else {
            style.display = 'none';
          }
        }

        return (
          <div className={itemClassName} style={style} key={widget.id}>
            {isRowWidget(widget) ? (
              <DashboardRowWidget
                widget={widget}
                pageFilters={pageFilters}
                onWidgetExpand={handleWidgetExpand}
              />
            ) : (
              <DashboardWidget
                widget={widget}
                pageFilters={pageFilters}
                isFullScreen={isFullScreen}
                draggableHandleSelector={isEditable && draggableHandleSelector}
                draggableIgnoreSelector={isEditable && draggableIgnoreSelector}
                onWidgetExpand={handleWidgetExpand}
              />
            )}
          </div>
        );
      }),
    [
      dashboard.widgets,
      pageFilters,
      fullscreenWidgetId,
      draggableHandleSelector,
      draggableIgnoreSelector,
      isEditable,
    ],
  );

  const isDraggable = width >= breakpoints[BREAKPOINT_MEDIUM] && isEditable;

  return (
    <div ref={containerRef} className="flex-1">
      <ResponsiveGridLayout
        className={cn(
          DASHBOARD_LAYOUT_CLASS,
          'relative transition-[height] duration-200 ease-in-out',
          animated && DASHBOARD_LAYOUT_ANIMATED_CLASS,
          fullscreenWidgetId != null &&
            cn(DASHBOARD_WIDGET_FULLSCREEN_CLASS, 'h-full! w-full! transition-none'),
          resizing && 'select-none',
          className,
        )}
        width={width}
        layouts={{ [BREAKPOINT_MEDIUM]: buildLayout(dashboard) }}
        breakpoints={breakpoints}
        cols={cols}
        margin={[32, 32]}
        containerPadding={[0, 0]}
        rowHeight={rowHeight}
        dragConfig={{
          enabled: isDraggable,
          handle: `.${draggableHandleSelector}`,
          cancel: `.${draggableIgnoreSelector}`,
        }}
        resizeConfig={{
          enabled: isEditable && fullscreenWidgetId == null,
        }}
        onLayoutChange={handleLayoutChange}
        onResizeStart={() => setResizing(true)}
        onResizeStop={() => setResizing(false)}
      >
        {children}
      </ResponsiveGridLayout>
    </div>
  );
};

export default DashboardLayout;
